import React, { useMemo, useState, useCallback } from "react";
import {
  Table,
  Card,
  Tag,
  Typography,
  Row,
  Col,
  Empty,
  Spin,
  Space,
  Statistic,
  Avatar,
  Button,
  Input,
} from "antd";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  LabelList,
} from "recharts";
import {
  TeamOutlined,
  MailOutlined,
  GlobalOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  UserOutlined,
  FlagOutlined,
  SearchOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

/**
 * Presentational Roster View. Receives data, summary, and callbacks from parent (e.g. RosterOrchestrator).
 * No service or API calls; all data is passed as props.
 */
const RosterView = ({
  data = [],
  loading = false,
  summary = null,
  showSummary = true,
  showPOName = false,
  projectId = null,
  projectObjectives = [],
  poTableLoading = false,
  selectedProjectObjectiveId = null,
  onPOSelect = null,
  summaryLoading = false,
}) => {
  const [statusFilter, setStatusFilter] = useState(null);
  const [poTableSearchQuery, setPOTableSearchQuery] = useState("");

  const effectiveData = data;
  const effectiveSummary = summary;
  const effectiveLoading = loading;
  const effectiveProjectObjectives = projectObjectives;
  const effectiveSelectedPOId = selectedProjectObjectiveId;
  // Format pay rate as currency
  const formatPayRate = (rate) => {
    if (rate === null || rate === undefined) return "N/A";
    return `$${Number(rate).toFixed(2)}`;
  };

  // Format tenure days as readable text
  const formatTenure = (days) => {
    if (days === null || days === undefined) return "N/A";
    if (days < 7) return `${days} day${days !== 1 ? "s" : ""}`;
    if (days < 30) {
      const weeks = (days / 7).toFixed(1);
      return `${weeks} week${weeks !== "1.0" ? "s" : ""}`;
    }
    const months = (days / 30).toFixed(1);
    return `${months} month${months !== "1.0" ? "s" : ""}`;
  };

  // Table columns
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: "Contributor Name",
        dataIndex: "contributor_name",
        key: "contributor_name",
        sorter: (a, b) =>
          (a.contributor_name || "").localeCompare(b.contributor_name || ""),
        width: 200,
        render: (text) => (
          <Space>
            <Avatar
              size="small"
              icon={<UserOutlined />}
              style={{ backgroundColor: "#1890ff" }}
            />
            <Text strong>{text || "-"}</Text>
          </Space>
        ),
      },
    ];

    // Add PO name column if showPOName is true (for project-level views)
    if (showPOName) {
      baseColumns.push({
        title: "Project Objective",
        dataIndex: "po_name",
        key: "po_name",
        width: 200,
        sorter: (a, b) => (a.po_name || "").localeCompare(b.po_name || ""),
        render: (text) => (
          <Space>
            <FlagOutlined style={{ color: "#722ed1" }} />
            <Text>{text || "N/A"}</Text>
          </Space>
        ),
      });
    }

    baseColumns.push(
      {
        title: "Email",
        dataIndex: "contributor_email",
        key: "contributor_email",
        width: 250,
        ellipsis: true,
        sorter: (a, b) =>
          (a.contributor_email || "").localeCompare(b.contributor_email || ""),
        render: (text) => (
          <Space>
            <MailOutlined style={{ color: "#8c8c8c" }} />
            <Text>{text || "-"}</Text>
          </Space>
        ),
      },
      {
        title: "Status",
        dataIndex: "cp_status",
        key: "cp_status",
        width: 120,
        filters: [
          { text: "Active", value: "Active" },
          { text: "Production", value: "Production" },
        ],
        onFilter: (value, record) => record.cp_status === value,
        render: (status) => (
          <Tag color={status === "Production" ? "green" : "blue"}>{status}</Tag>
        ),
      },
      {
        title: "Country",
        dataIndex: "contributor_country",
        key: "po_country",
        width: 120,
        sorter: (a, b) =>
          (a.contributor_country || "").localeCompare(
            b.contributor_country || "",
          ),
        render: (text) => (
          <Space>
            <GlobalOutlined style={{ color: "#8c8c8c" }} />
            <Text>{text || "N/A"}</Text>
          </Space>
        ),
      },
      {
        title: "Active Tenure",
        dataIndex: "active_tenure_days",
        key: "active_tenure_days",
        width: 150,
        sorter: (a, b) =>
          (a.active_tenure_days || 0) - (b.active_tenure_days || 0),
        render: (days) => (
          <Space>
            <ClockCircleOutlined style={{ color: "#8c8c8c" }} />
            <Text>{formatTenure(days)}</Text>
          </Space>
        ),
      },
      {
        title: "Pay Rate",
        dataIndex: "actual_pay_rate",
        key: "actual_pay_rate",
        width: 120,
        sorter: (a, b) => (a.actual_pay_rate || 0) - (b.actual_pay_rate || 0),
        render: (rate) => (
          <Space>
            <DollarOutlined style={{ color: "#52c41a" }} />
            <Text strong>{formatPayRate(rate)}</Text>
          </Space>
        ),
      },
    );

    return baseColumns;
  }, [showPOName]);

  // Filter data based on status filter
  const filteredData = useMemo(() => {
    if (!statusFilter) return effectiveData;
    return effectiveData.filter((item) => item.cp_status === statusFilter);
  }, [effectiveData, statusFilter]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!effectiveSummary) return [];
    return [
      {
        name: "Active",
        value: effectiveSummary?.active_count || 0,
        color: "#722ed1", // purple
      },
      {
        name: "Production",
        value: effectiveSummary?.production_count || 0,
        color: "#52c41a", // green
      },
    ];
  }, [effectiveSummary]);

  // Handle chart segment click
  const handleChartClick = (data, index, e) => {
    if (!data || !data.name) return;
    const clickedStatus = data.name;
    // Toggle: if already filtered by this status, clear filter; otherwise set it
    if (statusFilter === clickedStatus) {
      setStatusFilter(null);
    } else {
      setStatusFilter(clickedStatus);
    }
  };

  const handlePOSelect = useCallback(
    (po) => {
      if (!onPOSelect) return;
      if (effectiveSelectedPOId === po.project_objective_id) {
        onPOSelect(null);
      } else {
        onPOSelect(po);
      }
      setStatusFilter(null);
    },
    [onPOSelect, effectiveSelectedPOId],
  );

  // Filter project objectives based on search query
  const filteredProjectObjectives = useMemo(() => {
    if (!poTableSearchQuery.trim()) {
      return effectiveProjectObjectives;
    }
    const query = poTableSearchQuery.toLowerCase();
    return effectiveProjectObjectives.filter(
      (po) =>
        po.po_name?.toLowerCase().includes(query) ||
        po.project_objective_id?.toLowerCase().includes(query),
    );
  }, [effectiveProjectObjectives, poTableSearchQuery]);

  // Project objectives table columns
  const poTableColumns = useMemo(
    () => [
      {
        title: "PO Name",
        dataIndex: "po_name",
        key: "po_name",
        width: 200,
        fixed: "start",
        sorter: (a, b) => (a.po_name || "").localeCompare(b.po_name || ""),
        render: (text, record) => (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Text
              title={text}
              onClick={() => handlePOSelect(record)}
              style={{ width: 200, cursor: "pointer" }}
              ellipsis={{ defaultExpanded: false }}
            >
              {text || "N/A"}
            </Text>
            <Text
              style={{ fontSize: 8 }}
              copyable
              ellipsis={{ defaultExpanded: false }}
            >
              {record.project_objective_id || "N/A"}
            </Text>
          </div>
        ),
      },
      {
        title: "Language",
        dataIndex: "po_language",
        key: "po_language",
        width: 80,
        render: (text) => text || "N/A",
      },
      {
        title: "Country",
        dataIndex: "po_country",
        key: "po_country",
        width: 100,
        render: (text) => (
          <Text style={{ width: 80 }} ellipsis={{ defaultExpanded: false }}>
            {text || "N/A"}
          </Text>
        ),
      },
      {
        title: "Status",
        dataIndex: "po_status",
        key: "po_status",
        width: 80,
        sorter: (a, b) => (a.po_status || "").localeCompare(b.po_status || ""),
        render: (text) => (
          <Tag color={text === "Active" ? "green" : "blue"}>{text}</Tag>
        ),
      },
      {
        title: "Contribution %",
        dataIndex: "percentage",
        key: "percentage",
        width: 100,
        sorter: (a, b) => (a.percentage || 0) - (b.percentage || 0),
        render: (_, rec) => {
          return <div>{rec.percentage.toFixed(2)}%</div>;
        },
      },
      {
        title: "Active / Production",
        dataIndex: "production",
        key: "production",
        width: 110,
        render: (_, rec) => {
          return (
            <div
              width="100%"
              height={250}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <BarChart
                width="75%"
                height={40}
                layout="vertical"
                responsive
                barGap={0}
                data={[
                  {
                    name: "Production",
                    a: rec.active,
                    p: rec.production,
                  },
                ]}
              >
                <XAxis
                  label={false}
                  tick={false}
                  hide={true}
                  mirror
                  dataKey="p"
                  domain={[0, Math.max(rec.active, rec.production)]}
                  type="number"
                />
                <YAxis
                  label={false}
                  tick={false}
                  mirror
                  dataKey="name"
                  type="category"
                />
                <Bar
                  // radius={[0, 4, 4, 0]}
                  type="number"
                  dataKey="a"
                  fill="#0958d9"
                >
                  <LabelList
                    style={{ fill: "#fff" }}
                    value={rec.active}
                    position="insideRight"
                    offset={6}
                  />
                </Bar>
                <Bar
                  // radius={[0, 4, 4, 0]}
                  type="number"
                  dataKey="p"
                  fill="#52c41a"
                >
                  <LabelList
                    style={{ fill: "#fff" }}
                    value={rec.production}
                    position="insideRight"
                    offset={6}
                  />
                </Bar>
              </BarChart>
            </div>
          );
        },
      },
    ],
    [handlePOSelect],
  );

  // Render chart content (to be embedded in Total Contributors card)
  const renderChartContent = () => {
    if (!effectiveSummary || effectiveSummary?.total_cps === 0) return null;

    const COLORS = ["#0958d9", "#52c41a"];

    return (
      <>
        <ResponsiveContainer width="100%" height={190} style={{ flex: 3 }}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({
                percent,
                cx,
                cy,
                midAngle,
                innerRadius,
                outerRadius,
              }) => {
                if (
                  cx == null ||
                  cy == null ||
                  midAngle == null ||
                  innerRadius == null ||
                  outerRadius == null
                ) {
                  return null;
                }
                const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                const x = cx + radius * Math.cos(-midAngle * (Math.PI / 180));
                const y = cy + radius * Math.sin(-midAngle * (Math.PI / 180));
                return (
                  <text
                    position="right"
                    x={x}
                    y={y}
                    textAnchor="middle"
                    fill="white"
                  >
                    {`${(percent * 100).toFixed(0)}%`}
                  </text>
                );
              }}
              outerRadius={60}
              fill="#8884d8"
              dataKey="value"
              onClick={handleChartClick}
              style={{ cursor: "pointer" }}
            >
              <Legend align="right" layout="vertical" verticalAlign="middle" />
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                  opacity={
                    !statusFilter || entry.name === statusFilter ? 1 : 0.3
                  }
                />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
          {statusFilter && (
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <Tag
                color={statusFilter === "Production" ? "green" : "blue"}
                closable
                onClose={() => setStatusFilter(null)}
                style={{ fontSize: 12 }}
              >
                Filtered by: {statusFilter}
              </Tag>
              <Button
                type="link"
                size="small"
                onClick={() => setStatusFilter(null)}
                style={{ marginLeft: 8 }}
              >
                Clear filter
              </Button>
            </div>
          )}
        </ResponsiveContainer>
      </>
    );
  };

  // Render table view
  const renderTableView = () => (
    <Table
      loading={effectiveLoading}
      rowKey="contributor_project_id"
      dataSource={filteredData}
      columns={columns}
      scroll={{ y: "calc(90vh - 400px)" }}
      pagination={{
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "50", "100"],
        showTotal: (total, range) =>
          `${range[0]}-${range[1]} of ${total} contributor${
            total !== 1 ? "s" : ""
          }${statusFilter ? ` (filtered by ${statusFilter})` : ""}`,
      }}
      locale={{
        emptyText: (
          <Empty
            description="No contributors found"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ),
      }}
      rowClassName={(record, index) =>
        index % 2 === 0 ? "table-row-light" : "table-row-dark"
      }
    />
  );

  return (
    <div style={{ padding: "8px 32px" }}>
      {/* Project Objectives Table - Show when projectId is provided */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {projectId && (
          <Col xs={24} sm={12} md={11}>
            {projectId && (
              <Card
                styles={{
                  body: {
                    padding: 0,
                  },
                }}
                title={
                  <Space>
                    <FlagOutlined />
                    <Text strong>Project Objectives</Text>
                    {!poTableLoading && (
                      <Text type="secondary" style={{ fontSize: 14 }}>
                        {poTableSearchQuery
                          ? `Displaying ${filteredProjectObjectives.length} / ${effectiveProjectObjectives.length}`
                          : `# ${effectiveProjectObjectives.length}`}
                      </Text>
                    )}
                  </Space>
                }
                extra={
                  <Input
                    placeholder="Search by name or ID..."
                    prefix={<SearchOutlined />}
                    value={poTableSearchQuery}
                    onChange={(e) => setPOTableSearchQuery(e.target.value)}
                    style={{ width: 250 }}
                    allowClear
                  />
                }
              >
                {poTableLoading ? (
                  <div style={{ textAlign: "center", padding: "40px" }}>
                    <Spin tip="Loading project objectives..." />
                  </div>
                ) : filteredProjectObjectives.length === 0 ? (
                  <Empty
                    description={
                      effectiveProjectObjectives.length === 0
                        ? "No project objectives found for this project"
                        : "No project objectives match your search"
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <Table
                    size="small"
                    style={{
                      height: "200px",
                      width: "100%",
                      overflowY: "auto",
                    }}
                    bordered={true}
                    dataSource={filteredProjectObjectives}
                    columns={poTableColumns}
                    scroll={{ x: "250px" }}
                    pagination={false}
                    rowKey="project_objective_id"
                    rowClassName={(record) =>
                      effectiveSelectedPOId === record.project_objective_id
                        ? "selected-row"
                        : ""
                    }
                    sticky={true}
                  />
                )}
                <style>{`
            .selected-row {
              background-color: #bae7ff !important;
            }
          `}</style>
              </Card>
            )}
          </Col>
        )}

        {/* Summary Cards */}
        {
          <>
            <Col xs={24} sm={12} md={8}>
              <Card
                style={{
                  height: "100%",
                }}
                styles={{
                  body: {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  },
                }}
              >
                {summaryLoading && <Spin size="small" />}
                {!summaryLoading && (
                  <>
                    <Statistic
                      style={{
                        flex: 2,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                      title={<Text type="secondary">Total Contributors</Text>}
                      value={effectiveSummary?.total_cps || 0}
                      prefix={<TeamOutlined />}
                      valueStyle={{ color: "#722ed1" }}
                    />
                    {effectiveSummary?.total_cps > 0 && renderChartContent()}
                  </>
                )}
              </Card>
            </Col>
            <Col
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
              xs={24}
              sm={12}
              md={5}
            >
              <Row gutter={8}>
                <Col span={24}>
                  <Card>
                    <Statistic
                      title={<Text type="secondary">Active</Text>}
                      value={effectiveSummary?.active_count || 0}
                      prefix={<TeamOutlined />}
                      valueStyle={{ color: "#0958d9" }}
                    />
                  </Card>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={24}>
                  <Card>
                    <Statistic
                      title={<Text type="secondary">Production</Text>}
                      value={effectiveSummary?.production_count || 0}
                      prefix={<TeamOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Card>
                </Col>
              </Row>
            </Col>
          </>
        }
      </Row>

      {renderTableView()}

      <style>{`
        .table-row-light {
          background-color: #fafafa;
        }
        .table-row-dark {
          background-color: #ffffff;
        }
      `}</style>
    </div>
  );
};

export default RosterView;
